import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiVehicleShareService } from '../../../../../../core/services/api/Vehicle/api-vehicle-share';
import { ApiVehicleService } from '../../../../../../core/services/api/Vehicle/api-vehicle';
import { ToastCustomService } from '../../../../../../tools/services/toast/toast-custom/toastCustom.service';
import { MyVehicleResponse } from '../../../../../../core/models/information.model';

@Component({
  selector: 'app-modal-vehicle-share',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-vehicle-share.component.html',
  styleUrl: './modal-vehicle-share.component.css'
})
export class ModalVehicleShareComponent implements OnInit {
  @Input() uid: string = '';
  @Input() currentLang: 'TH' | 'EN' = 'TH';
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  searchQuery: string = '';
  foundUser: { cpUserId: number; name: string; phone: string } | null = null;
  myVehicles: MyVehicleResponse[] = [];
  selectedVehicleId: number = 0;

  // Custom dropdown search state
  vehicleSearchText = '';
  activeVehicleDropdown = false;
  isLoading = false;

  constructor(
    private apiVehicleShareService: ApiVehicleShareService,
    private apiVehicleService: ApiVehicleService,
    private toastCustomService: ToastCustomService
  ) { }

  ngOnInit() {
    this.loadMyVehicles();
  }

  /* ================= Normal Functions ================= */

  // Load user's own vehicles to populate select dropdown
  loadMyVehicles() {
    this.apiVehicleService.getMyVehicle('', this.uid).subscribe({
      next: (res) => {
        this.myVehicles = res || [];
        this.selectedVehicleId = 0;
        this.vehicleSearchText = '';
      },
      error: () => {
        this.myVehicles = [];
      }
    });
  }

  // Filter owned vehicles list in autocomplete custom dropdown
  get filteredVehicleOptions() {
    const text = this.vehicleSearchText.trim().toLowerCase();
    if (!text) return this.myVehicles;
    return this.myVehicles.filter(v => (v.name || '').toLowerCase().includes(text));
  }

  openVehicleDropdown() {
    this.activeVehicleDropdown = true;
  }

  closeVehicleDropdown() {
    setTimeout(() => {
      this.activeVehicleDropdown = false;
      const match = this.myVehicles.find(v => v.name === this.vehicleSearchText);
      if (match) {
        this.selectedVehicleId = match.id;
      } else {
        const current = this.myVehicles.find(v => v.id === this.selectedVehicleId);
        this.vehicleSearchText = current ? (current.name || '') : '';
      }
    }, 200);
  }

  selectVehicleOption(v: MyVehicleResponse) {
    this.selectedVehicleId = v.id;
    this.vehicleSearchText = v.name;
    this.activeVehicleDropdown = false;
  }

  // Sanitize and block non-numeric characters for phone input
  onSearchInput(event: any) {
    const val = event.target.value.replace(/[^0-9]/g, '');
    this.searchQuery = val;
    event.target.value = val;
  }

  // Retrieve user by phone number
  searchUser() {
    const query = this.searchQuery.trim();
    if (!query) {
      const th = 'กรุณากรอก Line ID หรือเบอร์โทรศัพท์เพื่อค้นหา';
      const en = 'Please enter Line ID or Phone number';
      this.toastCustomService.showWarning('', this.currentLang === 'TH' ? th : en);
      return;
    }

    this.isLoading = true;
    this.apiVehicleShareService.findUserByPhone(this.uid, query).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && res.cpUserId) {
          this.foundUser = {
            cpUserId: res.cpUserId,
            name: res.name || 'User',
            phone: res.phone || query
          };
        } else {
          this.foundUser = null;
          const th = 'ไม่พบข้อมูลผู้ใช้งาน';
          const en = 'User not found';
          this.toastCustomService.showError('', this.currentLang === 'TH' ? th : en);
        }
      },
      error: () => {
        this.isLoading = false;
        this.foundUser = null;
        const th = 'ไม่พบข้อมูลผู้ใช้งาน';
        const en = 'User not found';
        this.toastCustomService.showError('', this.currentLang === 'TH' ? th : en);
      }
    });
  }

  // Send share invite request
  sendShareInvite() {
    if (!this.foundUser) return;
    if (!this.selectedVehicleId) {
      const th = 'กรุณาเลือกทะเบียนรถที่จะแชร์';
      const en = 'Please select a vehicle to share';
      this.toastCustomService.showWarning('', this.currentLang === 'TH' ? th : en);
      return;
    }

    this.isLoading = true;
    this.apiVehicleShareService.inviteShareVehicle({
      uid: this.uid,
      vehicleId: this.selectedVehicleId,
      toCpUserId: this.foundUser.cpUserId
    }).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && (res.code === '000' || res.success)) {
          const th = 'ส่งคำเชิญแชร์รถสำเร็จ';
          const en = 'Share invitation sent successfully';
          this.toastCustomService.showSuccess(this.currentLang === 'TH' ? th : en, '');
          this.success.emit();
          this.close.emit();
        } else {
          this.toastCustomService.showError('', this.currentLang === 'TH' ? (res.message || 'ล้มเหลว') : (res.message_en || 'Failed'));
        }
      },
      error: () => {
        this.isLoading = false;
        const th = 'ส่งไม่สำเร็จ';
        const en = 'Failed to invite';
        this.toastCustomService.showError('', this.currentLang === 'TH' ? th : en);
      }
    });
  }
}
